import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import {
  FC,
  ChangeEvent,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { PartnerAddress, PartnerSharesCount } from 'types';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import normalizeEmail from 'utils/normalizeEmail';
import normalizeCif from 'utils/normalizeCif';
import { getFullAddress } from 'utils/filters';

import actionTypes from 'constants/actionTypes';
import draftTypes from 'constants/draftTypes';
import { cleanValue } from 'constants/formats';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import { setModal } from 'modules/_shared/redux/modalActions';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';
import { useGetPartnerOptions } from 'modules/partners/hooks/useGetPartnerOptions';
import { useGetHoldingClassOptions } from 'modules/partners/hooks/useGetHoldingClassesOption';
import { useGetDocuments } from 'modules/_shared/hooks/useGetDocuments';
import { addDocument } from 'modules/documents/redux/documentActions';
import { draftSafeStatus } from 'modules/drafts/constants/draftSafeStatus';
import { draftConvertibleNoteStatus } from 'modules/drafts/constants/draftConvertibleNoteStatus';

import { addDraftInvestorModalInitValue } from '../constants/addDraftInvestorModalInitValue';
import {
  AddDraftInvestorModalContextType,
  DraftConditions,
} from '../types/AddDraftInvestorModalContextType';

type AddDraftInvestorModalContextProps = {
  children: ReactNode;
  modalActionType: string;
  draftType: string;
  draftStatus: string;
  draftConditions?: DraftConditions;
  draftInvestorToEdit?: any; // Investor; TODO: fix type when refactoring drafts
  handleAddInvestorToDraft?: (investor: any) => void; // eslint-disable-line
  handleUpdateInvestorInDraft?: (investor: any) => void; // eslint-disable-line
  setTouched?: (touched: boolean) => void; // eslint-disable-line
};

const AddDraftInvestorModalContext =
  createContext<AddDraftInvestorModalContextType>(
    addDraftInvestorModalInitValue
  );

export const AddDraftInvestorModalProvider: FC<
  AddDraftInvestorModalContextProps
> = ({
  children,
  modalActionType,
  draftType,
  draftStatus,
  draftConditions,
  draftInvestorToEdit,
  handleAddInvestorToDraft,
  handleUpdateInvestorInDraft,
  setTouched,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const translationSuffix =
    draftType === draftTypes.DRAFT_CAPITAL_INCREASE ? '.FORCED' : '';

  const [isExistentPartner, setIsExistentPartner] = useState<boolean>(
    draftInvestorToEdit?.isPartnerAlready || false
  );
  const [name, setName] = useState<string>(draftInvestorToEdit?.name || '');
  const [cif, setCif] = useState<string>(draftInvestorToEdit?.cif || '');
  const [email, setEmail] = useState<string>(draftInvestorToEdit?.email || '');
  const [selectedPartner, setSelectedPartner] = useState<string>('');
  const [partnerActualSharesCount, setPartnerActualSharesCount] =
    useState<number>(draftInvestorToEdit?.shares?.initial || 0);
  const [partnerFutureSharesCount, setPartnerFutureSharesCount] =
    useState<number>(draftInvestorToEdit?.shares?.future || 0);
  const [partnerAddress, setPartnerAddress] = useState<
    PartnerAddress | undefined
  >();
  const [partnerNdPercent, setPartnerNdPercent] = useState<number>(
    draftInvestorToEdit?.NDInitial || 0
  );
  const [contribution, setContribution] = useState<number>(
    draftInvestorToEdit?.realContribution || 0
  );
  const [hasNotAdjustedContribution, setHasNotAdjustedContribution] =
    useState<boolean>(draftInvestorToEdit?.contributionNotRound || false);
  const [proportionalityType, setProportionalityType] = useState<string>(
    draftInvestorToEdit?.proportionality || ''
  );
  const [proportionalityPercent, setProportionalityPercent] = useState<number>(
    draftInvestorToEdit?.proportionalityPercent || 0
  );
  const [discountPercent, setDiscountPercent] = useState<number>(
    draftInvestorToEdit?.discountPercent || 0
  );
  const [hasDiscountByValuation, setHasDiscountByValuation] = useState<boolean>(
    draftInvestorToEdit?.discountByValuation || false
  );
  const [contributionDate, setContributionDate] = useState<string | null>(
    draftInvestorToEdit?.contributionDate
      ? new Date(draftInvestorToEdit.contributionDate)
          .toISOString()
          .split('T')[0]
      : null
  );
  const [file, setFile] = useState<File | undefined>();
  const [hasBeenEdited, setHasBeenEdited] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: partnerOptionsData, actions: partnerOptionsActions } =
    useGetPartnerOptions({
      societyId: actualSociety?._id || '',
      partnerId: draftInvestorToEdit?.societyPartnerId,
    });

  const {
    data: holdingClassOptionsData,
    actions: holdingClassOptionsActions,
    isLoading: isLoadingHoldingClassOptions,
  } = useGetHoldingClassOptions({
    societyId: actualSociety?._id || '',
    holdingClassId: draftInvestorToEdit?.holdingClass,
  });

  const { data: documentsData, isLoading: isLoadingDocuments } =
    useGetDocuments({
      documentIds: draftInvestorToEdit?.documents || [],
    });

  const resetInvestorFields = () => {
    setName('');
    setCif('');
    setEmail('');
    setSelectedPartner('');
    setPartnerActualSharesCount(0);
    setPartnerFutureSharesCount(0);
    setPartnerNdPercent(0);
    setProportionalityType('');
  };

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeIsExistentPartner = () => {
    setIsExistentPartner((prev) => !prev);
    resetInvestorFields();
  };

  const handleChangeName = (value: string) => {
    setName(value);
  };

  const handleChangeCif = (value: string) => {
    const normalicedCif = normalizeCif(value);
    setCif(normalicedCif);
  };

  const handleChangeEmail = (value: string) => {
    const normalicedEmail = normalizeEmail(value);
    setEmail(normalicedEmail);
  };

  const handleChangePartner = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    setSelectedPartner(selectedOption ? selectedOption.value : '');
    partnerOptionsActions.handleChangePartnerOption(selectedOption);
    setName(selectedOption?.name || '');
    setCif(selectedOption?.cif || '');
    setEmail(selectedOption?.email || '');
    setPartnerActualSharesCount(
      (selectedOption?.sharesCount as PartnerSharesCount)?.actual || 0
    );
    setPartnerFutureSharesCount(
      (selectedOption?.sharesCount as PartnerSharesCount)?.future || 0
    );
    setPartnerAddress(selectedOption?.address);

    const ndPercent =
      (((selectedOption?.sharesCount as PartnerSharesCount)?.actual || 0) *
        100) /
      (actualSociety?.sharesCount?.actual || 1);

    setPartnerNdPercent(ndPercent);
  };

  const handleChangeContribution = (event: ChangeEvent<HTMLInputElement>) => {
    const value = cleanValue(event.target.value);
    setContribution(value);
  };

  const handleChangeHasNotAdjustedContribution = () => {
    setHasNotAdjustedContribution((prev) => !prev);
  };

  const handleChangeProportionalityType = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setProportionalityType(event.target.value);
    setContribution(0);
    setProportionalityPercent(0);
  };

  const handleChangeProportionalityPercent = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const value = cleanValue(event.target.value);
    setProportionalityPercent(value);
  };

  const handleChangeDiscountPercent = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const value = cleanValue(event.target.value);
    setDiscountPercent(value);
  };

  const handleChangeHasDiscountByValuation = () => {
    setHasDiscountByValuation((prev) => !prev);
  };

  const handleChangeContributionDate = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setContributionDate(event.target.value);
  };

  const handleChangeFile = (newFile: File) => {
    setFile(newFile);
  };

  const buildUpdatedDocuments = (uploadedDocument: any) => [
    ...(draftInvestorToEdit?.documents || []),
    ...(uploadedDocument?._id ? [uploadedDocument._id] : []),
  ];

  const buildInvestorData = (documents: string[]) => ({
    name,
    email,
    cif,
    society: actualSociety['_id'],
    contribution,
    realContribution: contribution,
    discountPercent,
    discountByValuation: hasDiscountByValuation,
    shares: {
      initial: partnerActualSharesCount || 0,
      future: partnerFutureSharesCount || 0,
    },
    isPartnerAlready: isExistentPartner,
    proportionality: proportionalityType,
    proportionalityPercent,
    societyPartnerId: selectedPartner || null,
    carriedShares: draftInvestorToEdit?.carriedShares || 0,
    isHidden: draftInvestorToEdit?.isHidden,
    holdingClass: holdingClassOptionsData.selectedHoldingClass || null,
    documents,
  });

  const handleSaveDraftInvestor = async () => {
    try {
      setIsLoading(true);

      let uploadedDocument = null;

      if (file) {
        const newDocument = {
          file,
          date: new Date(),
          size: file.size,
          name: file.name,
          author: user['_id'],
          society: actualSociety['_id'],
          fileType: fileTypes[file.type as keyof typeof fileTypes],
          category: documentTypes.DOCUMENTS,
          description: t('DocumentLinkedToOperation'),
        };

        uploadedDocument = await store.dispatch(addDocument(newDocument));
      }

      const updatedDocuments = buildUpdatedDocuments(uploadedDocument);

      const newInvestor: any = buildInvestorData(updatedDocuments);

      if (
        [draftTypes.DRAFT_SAFE, draftTypes.DRAFT_CONVERTIBLE_NOTE].includes(
          draftType
        )
      ) {
        newInvestor.contributionDate = contributionDate;

        const activeStatus =
          draftType === draftTypes.DRAFT_SAFE
            ? draftSafeStatus.var.SAFE_ACTIVE
            : draftConvertibleNoteStatus.var.NC_ACTIVE;

        if (draftStatus === activeStatus) {
          newInvestor.investorId = draftInvestorToEdit?.investorId || null;
        }
      }

      if (draftType === draftTypes.DRAFT_CAPITAL_INCREASE) {
        newInvestor.contributionNotRound = !proportionalityType
          ? hasNotAdjustedContribution
          : false;
      }

      if (isExistentPartner) {
        newInvestor.address = getFullAddress(partnerAddress);
      }
      newInvestor.generatedKey = draftInvestorToEdit?.generatedKey || uuidv4();

      if (modalActionType === actionTypes.ADD) {
        handleAddInvestorToDraft?.(newInvestor);
      } else {
        handleUpdateInvestorInDraft?.(newInvestor);
      }

      setTouched?.(true);
      setIsLoading(false);

      const successMessage =
        modalActionType === actionTypes.EDIT
          ? t(`InvestorUpdatedSuccessfully${translationSuffix}`)
          : t(`InvestorAddedSuccessfully${translationSuffix}`);

      successSwal(successMessage);
      handleCloseModal();
    } catch (error) {
      setIsLoading(false);
      const errorMessage =
        modalActionType === actionTypes.EDIT
          ? t(`ErrorUpdatingInvestor${translationSuffix}`)
          : t(`ErrorAddingInvestor${translationSuffix}`);

      errorSwal(errorMessage);
      handleCloseModal();
    }
  };

  return (
    <AddDraftInvestorModalContext.Provider
      value={{
        data: {
          isExistentPartner,
          name,
          cif,
          email,
          partnerActualSharesCount,
          selectedPartner,
          selectedPartnerOption: partnerOptionsData.selectedPartnerOption,
          partnerNdPercent,
          partnerOptions: partnerOptionsData.partnerOptions,
          contribution,
          hasNotAdjustedContribution,
          proportionalityType,
          proportionalityPercent,
          discountPercent,
          hasDiscountByValuation,
          selectedHoldingClass: holdingClassOptionsData.selectedHoldingClass,
          selectedHoldingClassOption:
            holdingClassOptionsData.selectedHoldingClassOption,
          holdingClassOptions: holdingClassOptionsData.holdingClassOptions,
          contributionDate,
          file,
          documents: documentsData.documents,
          modalActionType,
          draftType,
          draftConditions,
          hasBeenEdited,
          isLoading:
            isLoading || isLoadingHoldingClassOptions || isLoadingDocuments,
        },
        actions: {
          handleCloseModal,
          handleChangeIsExistentPartner,
          handleChangeName,
          handleChangeCif,
          handleChangeEmail,
          handleChangePartner,
          handleSearchPartnerByTerm:
            partnerOptionsActions.handleSearchPartnerByTerm,
          handleChangeHasNotAdjustedContribution,
          handleChangeContribution,
          handleChangeProportionalityType,
          handleChangeProportionalityPercent,
          handleChangeDiscountPercent,
          handleChangeHasDiscountByValuation,
          handleChangeHoldingClass:
            holdingClassOptionsActions.handleChangeSelectedHoldingClass,
          handleChangeContributionDate,
          handleChangeFile,
          handleSearchHoldingClassByTerm:
            holdingClassOptionsActions.handleSearchHoldingClassByTerm,
          handleSaveDraftInvestor,
        },
      }}
    >
      {children}
    </AddDraftInvestorModalContext.Provider>
  );
};

export const useAddDraftInvestorModal = () => {
  const context = useContext(AddDraftInvestorModalContext);
  if (!context) {
    throw new Error(
      'useAddDraftInvestorModal must be used within a AddDraftInvestorModalProvider'
    );
  }
  return context;
};
